import RequiredAsterisk from 'components/RequiredAsterisk';
import { InputLabel, Autocomplete, TextField, CircularProgress } from '@mui/material';
import { useField } from 'formik';
import SelectAsyncFormFieldProps from './dto';
import { useEffect, useRef, useState } from 'react';
import axiosServices, { buildFilterParams } from 'utils/axios';
import { SelectOptionType } from 'utils/react-table';

const SelectAsyncFormField = ({
    label,
    isRequired = false,
    defaultOptions,
    source,
    optionMapperCb,
    params,
    multiple,
    initialValues,
    disabled = false,
    onChangeCallback,
    ...props
}: SelectAsyncFormFieldProps) => {
    const [field, meta, helpers] = useField(props);
    const [options, setOptions] = useState(defaultOptions ? defaultOptions : []);
    const [loading, setLoading] = useState<boolean>(!!defaultOptions);
    const [value, setValue] = useState<SelectOptionType | Array<SelectOptionType> | null>(multiple ? [] : null);

    const helpersRef = useRef(helpers);
    helpersRef.current = helpers;

    const hasError = Boolean(meta.touched && meta.error);

    useEffect(() => {
        if (source && optionMapperCb) {
            setLoading(true);
            axiosServices
                .get(`${source}${buildFilterParams(params)}`)
                .then((res) => {
                    const mappedOptions = optionMapperCb(Array.isArray(res.data) ? res.data : res.data.elements);
                    setOptions(mappedOptions);

                    if (initialValues) {
                        if (typeof initialValues == 'number') {
                            const value = mappedOptions.find((o) => o.value === initialValues) ?? null;
                            setValue(value);
                            helpersRef.current.setValue(value);
                        } else if (Array.isArray(initialValues)) {
                            const values = mappedOptions.filter((o) => initialValues.includes(Number(o.value)));
                            setValue(values);
                            helpersRef.current.setValue(values);
                        }
                    } else {
                        setValue(multiple ? [] : null);
                    }
                })
                .catch
                // TODO toast error
                ()
                .finally(() => setLoading(false));
        }
    }, [source, optionMapperCb, params, initialValues, multiple]);

    return (
        <>
            <InputLabel htmlFor={props.name}>
                {label} {isRequired && <RequiredAsterisk />}
            </InputLabel>
            <Autocomplete
                id={props.name}
                options={options}
                popupIcon={loading ? <CircularProgress size={20} /> : undefined}
                loading={loading}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, selectedValue) => {
                    return option.value === selectedValue.value;
                }}
                {...props}
                {...field}
                disabled={disabled}
                multiple={multiple}
                value={value}
                onChange={(_, option) => {
                    setValue(option);
                    helpers.setValue(option, true);
                    onChangeCallback?.(option);
                }}
                renderInput={(params) => (
                    <TextField error={hasError} helperText={hasError ? meta.error : ''} {...params} />
                )}
            />
        </>
    );
};

export default SelectAsyncFormField;
